import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import ActivityItem from "@/components/customers/ActivityItem";
import CustomerActivityTimeline from "@/components/customers/CustomerActivityTimeline";
import SectionCard from "@/components/customers/SectionCard";
import { useCustomerActivities } from "@/hooks/use-customer-activities";

/** Antall rader i «Siste kontakt». Hele loggen ligger bak «Logg». */
export const LAST_CONTACT_LIMIT = 5;

interface CustomerLastContactCardProps {
  customerId: string;
  onSendEmail?: (contactId: string | null) => void;
}

/**
 * «Siste kontakt» i sidekolonnen: de siste radene i kontaktloggen, med «Logg»
 * som åpner hele tidslinjen i en dialog. Nye samtaler, notater og e-post logges
 * fra «Logg kontakt» i heroen. Uten kontakt er kortet én linje.
 */
export default function CustomerLastContactCard({ customerId, onSendEmail }: CustomerLastContactCardProps) {
  const { data: rows = [], isLoading } = useCustomerActivities(customerId, { limit: LAST_CONTACT_LIMIT });
  const [open, setOpen] = useState(false);

  return (
    <>
      <SectionCard
        compact
        title="Siste kontakt"
        empty="Ingen kontakt logget ennå. «Logg kontakt» øverst starter historikken."
        actions={
          <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setOpen(true)} aria-label="Åpne kontaktloggen">
            Logg
          </Button>
        }
      >
        {isLoading ? (
          <Skeleton className="m-4 h-16" />
        ) : rows.length === 0 ? null : (
          <ul className="px-4">
            {rows.map((row) => (
              <ActivityItem key={row.id} row={row} compact />
            ))}
          </ul>
        )}
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Kontaktlogg</DialogTitle>
            <DialogDescription>All kontakt med kunden, nyeste først.</DialogDescription>
          </DialogHeader>
          <CustomerActivityTimeline customerId={customerId} onSendEmail={onSendEmail} />
        </DialogContent>
      </Dialog>
    </>
  );
}
